import {property, state} from 'lit/decorators.js';
import {equals, not, propOr} from 'ramda';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  identity,
  map,
  merge,
  NEVER,
  Observable,
  of,
  shareReplay,
  skip,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import {ActivityOption} from './activity-option';

import Decimal, {DecimalSource} from './break_infinity';
import * as D from './decimal_utils';
import {logged} from './decorators/logged';
import {Filterable} from './interfaces/filterable';
import {ri} from './runner-idle';
import {RxLitElement} from './rx-lit-element';
import {
  ActivityName,
  LandmarkName,
  ResourceName,
  StatName,
  UnlockableLitElementSaveData,
} from './types/runner-idle';
import {all$, any$, typedEntries} from './utils';

/**
 * An in-game unlockable thing.
 *
 * All UnlockableLitElements are real web actions,
 * but they are often not rendered until they are unlocked (or at all).
 * This is heavy, but it hasn't been a problem yet,
 * and it's pleasantly straightforward semantically:
 * the objects you unlock are the elements you see on the screen,
 * quite literally.
 * Most other game objects are subclasses of UnlockableLitElement,
 * adding functionality and properties as needed.
 * Certain places, where we need multiple views into the same model, this isn't ideal for.
 */
export class UnlockableLitElement extends RxLitElement implements Filterable {
  // #region Properties (43)

  /**
   * Backing property for .unlock$
   * Returned upon get unlock$,
   * and subscribes to values set to unlock$
   */
  readonly #persistentUnlock$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  /**
   * Backing property for .visible$
   * Returned upon get visible$,
   * and subscribes to values set to visible$
   */
  readonly #persistentVisible$: BehaviorSubject<boolean> = new BehaviorSubject(
    true
  );

  /** Subscription for values passed through #unlock$ */
  #unlockSubscription?: Subscription;
  /** Subscription for values passed through #visible$ */
  #visibleSubscription?: Subscription;

  protected _cheated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  /**
   * The base string for what is required to unlock this.
   * Used to include any nonstandardized unlock conditions.
   * */
  protected baseConditionString = '';
  /**
   * Backing property for tags getter/setter.
   * Tags associated with this object.
   * Used for filtering and more.
   */
  protected baseTags = new Set<string>(['unlockable']);
  /**
   * A stream representing the unlock state of this object.
   */
  protected baseUnlock$: Observable<boolean> = NEVER;
  /**
   * A stream representing the visible state of this object.
   */
  protected baseVisible$: Observable<boolean> = NEVER;

  /** Is this hidden due to filters? */
  @state() public readonly hiddenByFilter$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  /** A user-friendly name. */
  @property() public displayName = 'Unlockable';
  /** The internal name of this Unlockable. Unique within its class; e.g. Resource, Activity, etc. */
  @property() public override name = 'unlockable';
  /** Is the displayName of this hidden before being unlocked? */
  @property({type: Boolean}) public secret = false;
  /** Is this hidden by the player (or hidden and able to be unhidden by the player)? */
  @state() public hiddenByPlayer$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  /** Should the unlockableEntry for this always be hidden? */
  @state() public hideEntry = false;
  /** Backing prop for visible$ */
  @state() public visible = true;

  /**
   * The tags required on the currentLandmark to see/use this object.
   */
  public landmarkTags = new Set<string>();
  new$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  /** Is this unlocked post-reincarnation? */
  public persistentUnlock = false;
  /** Does this retain its value post-reincarnation? */
  public persistentValue = false;
  /** How close is this to being unlocked? */
  public progress$!: Observable<Decimal>;
  requiredResources!: Partial<Record<ResourceName, DecimalSource>>;
  requiredResources$: Observable<Partial<Record<ResourceName, DecimalSource>>> =
    of({null: 0});
  requiredStats!: Partial<Record<StatName, DecimalSource>>;
  requiredStats$: Observable<Partial<Record<StatName, DecimalSource>>> = of({
    null: 0,
  });
  requiredActivityLevels$: Observable<Partial<Record<string, DecimalSource>>> =
    of({
      null: 0,
    });
  requiredActivityLevels!: Partial<Record<ActivityName, DecimalSource>>;
  /** Should this turn invisible after being unlocked? */
  public temporary = false;
  /**
   * Has this already been unlocked then made invisible?
   * Should only ever be set to true if temporary is true.
   */
  public temporaryFinished$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  /** The tags required on the currentLandmark to unlock this object.  */
  public unlockAtTags = new Set<string>();
  public unlocked = false;
  requiredLandmarks$: Observable<Partial<Record<LandmarkName, boolean>>> = of(
    {}
  );
  requiredLandmarks!: {};
  /** A computed user-friendly string of  all the conditions required to unlock this.  */
  public get conditionString() {
    const sep = ', ';
    const returnString = [
      // this.unlockAtTags.size > 0
      //   ? `Be near ${[...this.unlockAtTags].join(' & ')}`
      //   : '',
      this.landmarkTags.size > 0
        ? `At: ${[...this.landmarkTags].join(' + ')}`
        : '',
      ...typedEntries(this.requiredLandmarks ?? {}).map(([k, v]) =>
        v
          ? `Visit ${ri.landmarks[k].displayName}`
          : `Do not visit ${ri.landmarks[k].displayName}`
      ),
      ...typedEntries(this.requiredStats ?? {})
        .filter(([k]) => k !== 'null')
        .map(([k, v]) =>
          v === 'unlocked'
            ? `Unlock ${ri.stats[k].shortName}`
            : `${ri.stats[k].shortName}: ${v}`
        ),
      ...typedEntries(this.requiredResources ?? {})
        .filter(([k]) => k !== 'null')
        .map(([k, v]) =>
          v === 'unlocked'
            ? `Unlock ${ri.resources[k].displayName}`
            : `${ri.resources[k].displayName}: ${v}`
        ),
      ...typedEntries(this.requiredActivityLevels ?? {})
        .filter(([k]) => k !== 'null')

        .map(([k, v]) =>
          v === 'unlocked'
            ? `Unlock ${ri.activities[k].displayName}`
            : `${ri.activities[k].displayName}: LVL${v}`
        ),
      this.baseConditionString,
    ]
      .filter(identity)
      .join(sep);
    return returnString !== '' ? returnString : 'No condition';
  }

  public set conditionString(value: string) {
    this.baseConditionString = value;
  }

  public get hiddenByFilter(): boolean {
    return this.hiddenByFilter$.value;
  }

  public set hiddenByFilter(value: boolean) {
    this.hiddenByFilter$.next(value);
  }

  public get hiddenByPlayer(): boolean {
    return this.hiddenByPlayer$.value;
  }

  public set hiddenByPlayer(value: boolean) {
    this.hiddenByPlayer$.next(value);
  }

  /** Is this newly unlocked? Adds some visual hints. Property synced to class 'new'. */
  public get new() {
    return this.new$.value;
  }

  public set new(value) {
    this.new$.next(value);
    this.classList.toggle('new', value);
  }

  /**
   * Tags are string flags representing various things.
   */
  public get tags() {
    return this.baseTags;
  }

  /**
   * Setter for tags.
   * Merges new value with existing value.
   */
  public set tags(val) {
    this.baseTags = new Set([...val, ...this.baseTags]);
  }

  public get unlock$(): Observable<boolean> {
    return this.#persistentUnlock$;
  }

  public set unlock$(value: Observable<boolean>) {
    if (!value) return;
    this.baseUnlock$ = any$([
      this._cheated$,
      all$([
        this.requiredLandmarks$.pipe(
          switchMap((landmarkDictionary) =>
            all$(
              Object.keys(landmarkDictionary).length > 0
                ? typedEntries(landmarkDictionary).map(([k, v]) => {
                    return ri.landmarks[k].visited$.pipe(map(equals(v)));
                  })
                : [of(true)]
            )
          )
        ),
        this.requiredStats$.pipe(
          switchMap((statDictionary) =>
            all$(
              typedEntries(statDictionary).map(([k, v]) => {
                switch (v) {
                  case 'unlocked':
                    return ri.stats[k].unlock$;
                  case 'any':
                    return ri.stats[k].value$.pipe(
                      map(D.neq(ri.stats[k].initialValue))
                    );
                  case 'anyEver':
                    return ri.stats[k].maxValue$.pipe(
                      map(D.neq(ri.stats[k].initialValue))
                    );
                  default:
                    return ri.stats[k].maxValue$.pipe(map(D.gte(v)));
                }
              })
            )
          )
        ),
        this.requiredResources$.pipe(
          switchMap((resourceDictionary) =>
            all$(
              typedEntries(resourceDictionary).map(([k, v]) => {
                switch (v) {
                  case 'unlocked':
                    return ri.resources[k].unlock$;
                  case 'any':
                    return ri.resources[k].value$.pipe(
                      map(D.neq(ri.resources[k].initialValue))
                    );
                  case 'anyEver':
                    return ri.resources[k].maxValue$.pipe(
                      map(D.neq(ri.resources[k].initialValue))
                    );
                  default:
                    return ri.resources[k].maxValue$.pipe(map(D.gte(v)));
                }
              })
            )
          )
        ),
        this.requiredActivityLevels$.pipe(
          switchMap((activityDictionary) =>
            all$(
              typedEntries(activityDictionary).map(([k, v]) => {
                switch (v) {
                  default:
                    return ri.activities[k].level$.pipe(map(D.gte(v)));
                }
              })
            )
          )
        ),

        value,
      ]),
    ]).pipe(distinctUntilChanged(), shareReplay(1));
    this.progress$ = combineLatest([
      this.requiredStats$,
      this.requiredResources$,
    ]).pipe(
      switchMap(([statDictionary, resourceDictionary]) =>
        combineLatest([
          ...typedEntries(statDictionary).map(([k, v]) =>
            v === 'unlocked'
              ? ri.stats[k].unlock$.pipe(map((val) => (val ? 1 : 0)))
              : ri.stats[k].maxValue$.pipe(
                  map((val) => val.div(v).max(0).min(1))
                )
          ),
          ...typedEntries(resourceDictionary).map(([k, v]) =>
            v === 'unlocked'
              ? ri.resources[k].unlock$.pipe(map((val) => (val ? 1 : 0)))
              : ri.resources[k].maxValue$.pipe(
                  map((val) => val.div(v).max(0).min(1))
                )
          ),
        ])
      ),
      map(D.mean)
    );

    this.#unlockSubscription?.unsubscribe();
    this.#unlockSubscription = this.baseUnlock$.subscribe((unlocked) =>
      this.#persistentUnlock$.next(unlocked)
    );
  }

  /**
   * A stream representing the visibility state of this object.
   * Is this visible right now?
   */
  public get visible$(): Observable<boolean> {
    return this.#persistentVisible$;
  }

  public set visible$(value: Observable<boolean>) {
    if (!value) return;
    this.baseVisible$ = any$([
      this._cheated$,
      all$([
        this.requiredLandmarks$.pipe(
          switchMap((landmarkDictionary) =>
            all$(
              Object.keys(landmarkDictionary).length > 0
                ? typedEntries(landmarkDictionary).map(([k, v]) => {
                    return ri.landmarks[k].navigatable$.pipe(
                      map((navigatable) => navigatable || !v)
                    );
                  })
                : [of(true)]
            )
          )
        ),
        this.requiredStats$.pipe(
          switchMap((statDictionary) =>
            all$(
              typedEntries(statDictionary).map(([k, v]) =>
                k === 'null'
                  ? of(true)
                  : all$([
                      ri.stats[k].unlock$,
                      ri.stats[k].value$.pipe(
                        map((value) => new Decimal(v).div(4).lte(value))
                      ),
                    ])
              )
            )
          )
        ),
        this.requiredResources$.pipe(
          switchMap((resourceDictionary) =>
            all$(
              typedEntries(resourceDictionary).map(([k, v]) =>
                k === 'null'
                  ? of(true)
                  : all$([
                      ri.resources[k].unlock$,
                      ri.resources[k].value$.pipe(
                        map((value) => new Decimal(v).div(4).lte(value))
                      ),
                    ])
              )
            )
          )
        ),
        value,
      ]),
    ]).pipe(distinctUntilChanged(), shareReplay(1));
    this.#visibleSubscription?.unsubscribe();
    this.#visibleSubscription = this.baseVisible$.subscribe((visible) =>
      this.#persistentVisible$.next(visible)
    );
  }

  // #endregion Properties (43)

  // #region Constructors (1)

  constructor() {
    super();
    this.progress$ = combineLatest([this.unlock$]).pipe(
      map(([unlocked]) => (unlocked ? new Decimal(1) : new Decimal(0)))
    );
  }

  // #endregion Constructors (1)

  // #region Public Methods (8)

  /**
   * Setup in-game objectssetup, like Resource, Stats, etc.
   * Consider restructuring how the classes are setup, it's poor right now.
   */
  public setup(overrides: Partial<UnlockableLitElement> = {}): void {
    Object.assign(this, overrides);
    // Call unlock$ setter if we didn't manually set it,
    // to properly use the other condition (requiredStats, etc.).
    if (
      overrides.requiredLandmarks$ ||
      overrides.requiredStats$ ||
      overrides.requiredResources$
    ) {
      if (!overrides.unlock$) {
        Object.assign(this, {unlock$: of(true)});
      }
      if (!overrides.visible$) {
        Object.assign(this, {visible$: of(true)});
      }
    }
    if (this.name !== '') {
      this.classList.add(this.name.replace(/\s/g, '-'));
    }
    // Hide on ctrl click
    this.leftClick$
      .pipe(filter((clickEvent) => clickEvent.ctrlKey))
      .subscribe(() => this.toggleHiddenByPlayer());
    // Do this.onUnlock on unlock
    this.unlock$
      .pipe(distinctUntilChanged(), filter(identity))
      .subscribe(() => {
        // console.log(`Unlocked ${this.name}`);
        if (this.displayName !== 'Unlockable') {
          // Don't log options if you unlock them before unlocking the activity
          // NB: can't use instanceof ActivityOption here, because it's parent class is not defined yet
          if (!this.activity || this.activity?.unlocked) {
            ri.log(
              `Unlocked ${this.displayName}, condition: ${this.conditionString}`,
              ['unlockable', this.name, 'persistent']
            );
          }
        }
        if (!ri.isDragging) ri.masonry?.shiftLayout();
        this.new = this.new && !this.tags.has('landmark');
        this.secret = false;
        Object.values(ri.activities)
          .filter((activity) => activity.options.includes(this.name))
          .forEach((activity) => activity.requestUpdate());
        Object.values(ri.spells)
          .filter((spell) => spell.options.includes(this.name))
          .forEach((spell) => spell.requestUpdate());
        if (this.temporary) {
          this.temporaryFinished$.next(true);
        }
        this.onUnlock();
      });
    // Set initial hidden state
    this.toggleHiddenByPlayer(this.hiddenByPlayer);
    // Clear new when interacting with the element
    merge(this.mouseOver$, this.touchStart$).subscribe(
      this.clearNew.bind(this)
    );
    this.subscribe('unlocked', this.unlock$);
    this.subscribe(
      'hidden',
      any$([
        this.unlock$.pipe(map(not)),
        // this.hiddenByPlayer$,
        this.hiddenByFilter$,
      ]).pipe(distinctUntilChanged())
    );
    this.subscribe('visible', this.visible$);
    this.subscribe('requiredLandmarks', this.requiredLandmarks$);
    this.subscribe('requiredStats', this.requiredStats$);
    this.subscribe('requiredResources', this.requiredResources$);
    this.subscribe('requiredActivityLevels', this.requiredActivityLevels$);
  }

  // /** Is this unlockable right now? */
  // @state() public condition(): boolean {
  //   return (
  //     [...this.unlockAtTags].every((tag) =>
  //       ri.currentLandmark?.tags.has(tag)
  //     ) &&
  //     typedEntries(this.requiredLandmarks).reduce(
  //       (acc, [k, v]) => acc && ri.landmarks[k].unlocked === v,
  //       true
  //     ) &&
  //     typedEntries(this.requiredStats).reduce(
  //       (acc, [k, v]) => acc && ri.stats[k].value.gte(v),
  //       true
  //     ) &&
  //     typedEntries(this.requiredResources).reduce(
  //       (acc, [k, v]) => acc && ri.resources[k].value.gte(v),
  //       true
  //     )
  //   );
  // }

  /**
   * Are the landmarkTags satisfied?
   * @param step The step of the activity to check
   * @param optionIndex The index of the option to check
   * @returns True if this can be seen/used by landmarkTags
   */
  public canUseHere(_step?: number, _optionIndex?: number): boolean {
    return (
      this.landmarkTags.size === 0 ||
      [...this.landmarkTags].every((tag) => ri.pointHasTag(ri.position, tag))
    );
  }

  /**
   * Clear the 'new' status of this object.
   * Can be overwritten by subclasses to provide extra functionality,
   * e.g. Activities also clear the new of their options. */
  public clearNew() {
    this.new = false;
    ri.requestUpdate();
  }

  public override connectedCallback(): void {
    super.connectedCallback();
  }

  public loadFromJSON(data: UnlockableLitElementSaveData) {
    this.name = data.name;
    this.secret = data.secret;
    // Order matters. Setting this.unlocked to true looks at this.new in a subscription.
    // Not great, but it's only for preventing things from looking new every reload.
    this.new = data.new;
    this._cheated$.next(data.unlocked);
    this.unlocked = data.unlocked;
    this.toggleHiddenByPlayer(data.hiddenByPlayer);
  }

  public toJSON(): {} {
    return {
      name: this.name,
      secret: this.secret,
      unlocked: this.unlocked,
      new: this.new,
      hiddenByPlayer: this.hiddenByPlayer,
    };
  }

  /**
   * Toggle whether this is hidden in a player-controllable way.
   * Argument allows for setting instead of toggling.
   *
   * @param force set value if true/false, toggle if undefined
   */
  public toggleHiddenByPlayer(force?: boolean) {
    if (force !== undefined) {
      this.hiddenByPlayer = force;
    } else {
      this.hiddenByPlayer = !this.hiddenByPlayer;
    }
    this.classList.toggle('hiddenByPlayer', this.hiddenByPlayer);
    ri.requestUpdate();
  }

  public tryUnlock(cheat = false, quiet = false) {
    if (cheat) {
      this._cheated$.next(true);
      return true;
    }
    return false; // DANGER TESTING HACK TODO!
  }

  // #endregion Public Methods (8)

  // #region Protected Methods (1)

  // /** Is this visible right now? */
  // public visibleCondition(): boolean {
  //   if (this.temporaryFinished) return false;
  //   if (this.lockCondition()) return false;
  //   // if (this.condition()) return true;
  //   if (
  //     Object.keys(this.requiredLandmarks).length === 0 ||
  //     Object.keys(this.requiredStats).length +
  //       Object.keys(this.requiredResources).length >
  //       0
  //   ) {
  //     return (
  //       typedEntries(this.requiredLandmarks).reduce(
  //         (acc, [k, v]) => acc && ri.landmarks[k].visible === v,
  //         this._visibleCondition()
  //       ) &&
  //       typedEntries(this.requiredStats).reduce(
  //         (acc, [k]) => acc && ri.stats[k].unlocked,
  //         this._visibleCondition()
  //       ) &&
  //       typedEntries(this.requiredResources).reduce(
  //         (acc, [k]) => acc && ri.resources[k].unlocked,
  //         this._visibleCondition()
  //       ) &&
  //       typedEntries(this.visibleLandmarks).reduce(
  //         (acc, [k]) => acc && ri.landmarks[k].unlocked,
  //         this._visibleCondition()
  //       ) &&
  //       typedEntries(this.visibleStats).reduce(
  //         (acc, [k, v]) => acc && ri.stats[k].value.gte(v),
  //         this._visibleCondition()
  //       ) &&
  //       typedEntries(this.visibleResources).reduce(
  //         (acc, [k, v]) => acc && ri.resources[k].value.gte(v),
  //         this._visibleCondition()
  //       )
  //     );
  //   }
  //   return false;
  // }

  // /**
  //  * How close is this to being unlocked?
  //  * Internal method that does not have to stay at 100%,
  //  * the public version of this "property" first uses Unlockable.prototype.unlocked
  //  * @returns value in interval [0,1] depending on completion
  //  */
  // protected _progress(): Decimal {
  //   return typedEntries(this.requiredLandmarks)
  //     .reduce(
  //       (acc, [k]): Decimal => acc.plus(ri.landmarks[k].progress()),
  //       new Decimal(0)
  //     )
  //     .plus(
  //       typedEntries(this.requiredStats)
  //         .reduce(
  //           (acc, [k, v]) => acc.plus(ri.stats[k].value.div(v).min(1)),
  //           new Decimal(0)
  //         )
  //         .plus(
  //           typedEntries(this.requiredResources).reduce(
  //             (acc, [k, v]) => acc.plus(ri.resources[k].value.div(v).min(1)),
  //             new Decimal(0)
  //           )
  //         )
  //     )
  //     .div(
  //       typedEntries(this.requiredLandmarks).length +
  //         typedEntries(this.requiredStats).length +
  //         typedEntries(this.requiredResources).length
  //     );
  // }

  // /** The base condition for whether this is visible right now. */
  // protected _visibleCondition(): boolean {
  //   return true;
  // }

  /**
   * A function that does anything extra upon unlock.
   * To be overwritten by subclasses.
   */
  protected onUnlock() {
    // console.log(this.name, 'onUnlock called');
  }

  // #endregion Protected Methods (1)
}
